// Wave 20.65 — Public /liability page.
//
// The liability release was the only document a user could not read.
// It is the one with recorded consent, shown once inside the blocking
// accept modal and then unreachable: no route, no link, no way back
// to it. AIClientVision has shipped a "Re-accept liability" button
// pointing here since Wave 20.17, against a route that did not exist.
//
// Same shape as Terms.tsx and Privacy.tsx — one fetch of
// GET /public/legal/liability, one card, no copy in the client, so a
// new version published by migration appears here with no web deploy.
//
// Allowlisted in LiabilityGuard specifically so a user who has NOT
// accepted can still read it without the gate modal covering it.

import React, { useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  Container,
  Stack,
  Typography,
} from '@mui/material';
import PageLayout from './components/PageLayout';
import { colors } from './theme';

const API = process.env.REACT_APP_API || 'https://api.aero.chat';

interface LegalDoc {
  kind: 'liability';
  version: string;
  effectiveAt: string;
  body: string;
}

export default function Liability() {
  const [doc, setDoc] = useState<LegalDoc | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch(`${API}/public/legal/liability`)
      .then(async (r) => {
        if (!r.ok) throw new Error(`${r.status}`);
        setDoc(await r.json());
      })
      .catch((e) => setError(e?.message || 'Failed to load'));
  }, []);

  return (
    <PageLayout maxWidth="md">
      <Container maxWidth="md" sx={{ py: 2 }}>
        <Stack spacing={2}>
          <Box>
            <Typography variant="overline" sx={{ color: colors.brand, letterSpacing: '0.2em' }}>
              AeroChat Inc.
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 900 }}>
              Liability Release &amp; Waiver
            </Typography>
            {doc && (
              <Typography sx={{ color: colors.textMuted, mt: 0.5, fontSize: '0.85rem' }}>
                Effective {new Date(doc.effectiveAt).toLocaleDateString()}
              </Typography>
            )}
          </Box>

          {error && (
            <Typography sx={{ color: colors.danger, fontSize: '0.9rem' }}>
              Couldn't load the liability release: {error}
            </Typography>
          )}

          {!doc && !error && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress sx={{ color: colors.brand }} />
            </Box>
          )}

          {doc && (
            <Box
              sx={{
                p: { xs: 3, md: 4 },
                borderRadius: 3,
                backgroundColor: colors.surface,
                border: `1px solid ${colors.border}`,
                whiteSpace: 'pre-wrap',
                lineHeight: 1.7,
                color: colors.text,
                fontSize: '0.95rem',
              }}
            >
              {doc.body}
            </Box>
          )}
        </Stack>
      </Container>
    </PageLayout>
  );
}
